<template>
  <div :ref="inputRef" class="reg-information py-2">
    <v-row align="center">
      <v-col>
        <label>
          {{ field.label }}
        </label>
      </v-col>
      <v-chip v-if="field.requireSign"
              class="ma-2"
              dark
              small
              :color="signatureColor">
        <v-icon x-small>
          mdi-pencil
        </v-icon>
        {{ signatureText }}
      </v-chip>
      <reg-dynamic-menu v-if="file.id && field.canDelete" 
                        :options="[
                          { id: 'excluir', title: 'Excluir', icon: 'mdi-delete', disabled: false, visible: true }
                        ]" 
                        @excluir="doDeleteFile" />
    </v-row>
    <v-card tile color="file-card elevation-0">
      <v-card-text>
        <template v-if="file.id && (file.url || file.urlSigned)">
          <p v-if="file.url" class="screen-only">
            <a class="reg-link-button"
               @click="downloadFile(file, false)">
              <v-icon color="primary" small>
                file_download
              </v-icon>
              Download do arquivo gerado
            </a>
          </p>
          <p v-if="file.urlSigned && field.requireSign" class="screen-only  mt-2">
            <a class="reg-link-button"
               @click="downloadFile(file, true)">
              <v-icon color="primary" small>
                file_download
              </v-icon>
              Download do arquivo assinado
            </a>
          </p>
          <p v-if="!file.urlSigned && field.requireSign" class="screen-only waiting-text mt-2">
            Aguardando assinatura...
          </p>
          <p class="print-only">
            {{ file.url }}
          </p>
        </template>
        <p v-else-if="!field.requireSign">
          Nenhum Arquivo Gerado
        </p>
        <v-btn v-if="field.requireSign && !file.signatureStatus"
               :loading="$isLoading('sign-document' + field.id)"
               outlined
               block
               @click="onRequestSign">
          <v-icon>mdi-draw</v-icon>
          <span>Solicitar assinatura</span>
        </v-btn>
        <p v-if="$isLoading('sign-document' + field.id)">
          <v-icon color="primary" small>
            mdi-file-export-outline
          </v-icon>
          Gerando arquivo e solicitando assinatura...
        </p>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, getCurrentInstance, toRef } from 'vue'
import { useDynamicField } from '../composables/useDynamicField'
import { useDownload } from '../composables/useDownload'
import DynamicMenu from '~shared/DynamicMenu.vue'
import { isEmpty, isArray } from 'lodash'

export default defineComponent({
  name: 'DynamicGeneratedFileField',

  components: {
    'reg-dynamic-menu': DynamicMenu
  },

  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    field: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    box: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    isValidating: {
      type: Boolean,
      default: false
    },
    itemKey: {
      type: String,
      default: ''
    }
  },

  emits: ['input', 'change', 'request-sign', 'document-deleted'],

  setup(props, { emit, expose }) {
    const instance = getCurrentInstance()
    const $store = instance?.proxy?.$store
    const $t = instance?.proxy?.$t || ((key) => key)
    const confirmService = instance?.proxy?.$confirm
    const toasted = instance?.proxy?.$toasted
    const loggedUser = instance?.proxy?.$loggedUser
    const isLoading = instance?.proxy?.$isLoading

    const {
      fieldValue, 
      setFieldValue,
      inputRef,
      hasError
    } = useDynamicField({
      value: toRef(props, 'value'),
      field: props.field,
      readOnly: props.readOnly,
      isValidating: toRef(props, 'isValidating'),
      itemKey: props.itemKey,
      emptyValue: null,
      emit
    })

    const { downloadFileFromS3, downloadFileFromZapSign } = useDownload()

    // State
    const file = ref({})

    // Computed properties
    const signatureText = computed(() => {
      if (file.value.signatureStatus === 'signed') {
        return 'Assinado'
      } else if (file.value.signatureToken) {
        return 'Solicitado'
      } else {
        return 'Pendente'
      }
    })

    const signatureColor = computed(() => {
      if (file.value.signatureStatus === 'signed') {
        return 'green'
      } else if (file.value.signatureToken) {
        return 'warning'
      } else {
        return 'red'
      }
    })

    // Methods
    const deleteDocument = (payload) => {
      return $store.dispatch('deleteDocument', payload)
    }

    const downloadFile = (document, useSignedFile) => {
      const isLandDocument = document.landDocuments?.nodes?.length > 0
      const docType = document.documentType
      let customName = docType?.name || 'document'
      
      if (isLandDocument) {
        const land = document.landDocuments.nodes[0]?.land || {}
        const block = land?.block
        const lot = land?.lot
        const subdivision = land?.subdivision
        const landKey = `Quadra: ${block}- Lote: ${lot}${subdivision ? '-' + subdivision : ''}`
        customName = `${landKey} - ${docType?.name || 'document'}`
      } else {
        const resident = document.residentDocuments?.nodes?.[0]?.resident || {}
        customName = `${resident.name || 'resident'} - ${docType?.name || 'document'}`
      }

      if (document.url?.indexOf('zapsign') !== -1) {
        if (useSignedFile) {
          customName = `${customName} - Assinado.pdf`
        } else {
          customName = `${customName} - Original.pdf`
        }
        downloadFileFromZapSign(document, useSignedFile, customName)
      } else {
        const fileExtension = document.url?.split('.').pop() || 'pdf'
        customName = `${customName}.${fileExtension}`
        downloadFileFromS3(document, customName)
      }
    }

    const updateFieldValue = () => {
      if (isEmpty(fieldValue.value)) {
        file.value = {}
      } else {
        file.value = isArray(fieldValue.value) ? fieldValue.value[0] : fieldValue.value
      }
    }

    const onRequestSign = () => {
      emit('request-sign', {
        field: props.field,
        fieldValue: fieldValue.value
      })
    }

    const doDeleteFile = () => {
      const message = file.value.signatureStatus === 'signed' 
        ? 'Este documento já foi assinado, deseja realmente excluí-lo?' 
        : 'Deseja realmente excluir este documento?'
      const docId = file.value.id
      
      confirmService({
        title: $t('warning'),
        message,
        confirmText: $t('yes'),
        cancelText: $t('no')
      }).then((confirmed) => {
        if (!confirmed) return
        deleteDocument({
          id: docId,
          deletedAt: new Date().toISOString(),
          deletedBy: loggedUser.id
        }).then(() => {
          toasted.success($t('success_delete_file'))
          emit('document-deleted', docId)
        })
        file.value = {}
        setFieldValue(null)
      })
    }

    // Watchers
    watch(fieldValue, () => {
      updateFieldValue()
    })

    // Lifecycle
    onMounted(() => {
      updateFieldValue()
    })

    // Expose properties for DynamicForm validation
    expose({
      hasError,
      validate: () => inputRef.value?.validate?.()
    })

    return {
      fieldValue,
      file,
      signatureText,
      signatureColor,
      downloadFile,
      onRequestSign,
      doDeleteFile,
      inputRef
    }
  }
})
</script>

<style lang="scss">
  .file-size-style {
    text-align: right;
  }
  .waiting-text {
    color: #000 !important;
    font-size: 14px  !important;
  }
</style>